import { Button } from '@ads/components-react';
import { CalendarDateRange, ChevronLeft, ChevronRight } from '@ads/icons';
import { brandLinka } from '@ads/tokens';
import type { SessionMetricsDataDto } from '@linka/domain/dtos';
import { useSessionMetrics } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { cn, formatDate } from '@linka/utils';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';
import { useState } from 'react';
import { DayPicker } from 'react-day-picker';
import { useNavigate, useParams } from 'react-router';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import { Popover, PopoverContent, PopoverTrigger } from '../../../popover/popover';
import { Skeleton } from '../../../skeleton/skeleton';
import {
  ChartCard,
  ChartCardContent,
  ChartCardFooter,
  ChartCardHeader,
  ChartCardTitle,
  ExportChartTriggerButton,
} from '../components';

const dayPickerClassNames = {
  months: 'flex flex-col',
  month: 'flex flex-col p-nano self-center',
  caption: 'flex justify-center relative items-center h-[32px] mb-quark',
  caption_label: 'ts-subtitle-xxs text-ctx-content-title capitalize',
  nav: 'flex items-center',
  nav_button: cn(
    'absolute flex items-center justify-center h-[28px] w-[28px]',
    'text-ctx-content-base',
    'disabled:pointer-events-none disabled:opacity-40',
  ),
  nav_button_previous: 'left-0',
  nav_button_next: 'right-0',
  table: 'w-full border-collapse',
  head_row: 'flex mb-quark',
  head_cell: 'w-[36px] text-center ts-caption-xxxs text-ctx-content-base',
  row: 'flex w-full mt-quark',
  cell: 'relative p-0 text-center',
  day: cn('h-[36px] w-[36px] ts-paragraph-xs rounded-sm', 'text-ctx-content-title hover:bg-base-primary-100'),
  day_selected: 'bg-ctx-content-button rounded-sm hover:bg-ctx-content-button !text-base-neutral-white',
  day_today: '',
  day_outside: '!text-base-gray-400 hover:!text-ctx-content-title',
  day_disabled: '',
  day_hidden: '',
};

export const SessionsChartWrapper = () => {
  const { eventId = '' } = useParams();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  const dateParam = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined;
  const { data, isLoading, isError } = useSessionMetrics(eventId, dateParam);

  if (isLoading) return <Skeleton className='min-h-[400px]' />;
  if (isError || !data) return null;
  if (!data.data.length && !selectedDate) return null;

  return <SessionsChart data={data} selectedDate={selectedDate} onDateChange={setSelectedDate} />;
};

interface SessionsChartProps {
  data: NonNullable<ReturnType<typeof useSessionMetrics>['data']>;
  selectedDate: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
}

type SessionAxisTickProps = {
  x?: number;
  y?: number;
  payload?: { value: string };
  fill?: string;
};

const SessionAxisTick = ({ x = 0, y = 0, payload, fill }: SessionAxisTickProps) => {
  const parts = (payload?.value ?? '').split(' - ');
  return (
    <g transform={`translate(${x},${y})`}>
      <text textAnchor='middle' fill={fill} fontSize={11}>
        <tspan x='0' dy='12'>
          {parts[0]}
        </tspan>
        <tspan x='0' dy='14'>
          {parts[1]}
        </tspan>
      </text>
    </g>
  );
};

const SessionsChart = ({ data, selectedDate, onDateChange }: SessionsChartProps) => {
  const navigate = useNavigate();
  const t = useTranslation();
  const [open, setOpen] = useState(false);

  const chartConfig = {
    enrolled: { label: t('dashboard.detailed.enrolled'), color: brandLinka.baseColor.brand['400'] },
    available: { label: t('dashboard.detailed.available'), color: brandLinka.contextualColor.interactive.primary },
  };

  const chartData = data.data.map((session: SessionMetricsDataDto) => ({
    label: `${formatDate({ date: session.startDate, dateFormat: 'HH:mm' })} - ${formatDate({ date: session.endDate, dateFormat: 'HH:mm' })}`,
    enrolled: session.enrolled,
    available: session.capacity !== null ? Math.max(0, session.capacity - session.enrolled) : 0,
  }));

  const handleSelectDate = (date: Date | undefined) => {
    onDateChange(date);
    setOpen(false);
  };

  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.sessions')}</ChartCardTitle>
        <div className='flex items-center gap-nano'>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                type='button'
                hierarchy='tertiary'
                size='sm'
                leadingIcon={CalendarDateRange}
                className='!w-auto border border-ctx-layout-border !text-ctx-content-base whitespace-nowrap'
              >
                <span className='ts-caption-xxxs sm:ts-caption-xxs'>
                  {selectedDate ? format(selectedDate, 'dd/MM/yyyy') : t('dashboard.sessions.all-dates')}
                </span>
              </Button>
            </PopoverTrigger>
            <PopoverContent className='w-auto p-0' align='end'>
              <DayPicker
                mode='single'
                selected={selectedDate}
                onSelect={handleSelectDate}
                locale={pt}
                showOutsideDays
                components={{
                  IconLeft: () => <ChevronLeft size={16} />,
                  IconRight: () => <ChevronRight size={16} />,
                }}
                classNames={dayPickerClassNames}
              />
              {selectedDate && (
                <div className='border-t border-ctx-layout-border p-nano'>
                  <Button
                    type='button'
                    size='sm'
                    fullWidth
                    hierarchy='secondary'
                    onClick={() => handleSelectDate(undefined)}
                  >
                    {t('clear-filters')}
                  </Button>
                </div>
              )}
            </PopoverContent>
          </Popover>
          <ExportChartTriggerButton type='OVERVIEW_SESSIONS' />
        </div>
      </ChartCardHeader>

      <ChartCardContent className='flex flex-col'>
        {chartData.length === 0 ? (
          <div className='flex flex-1 flex-col items-center justify-center gap-sm py-xl'>
            <span className='ts-paragraph-xs text-ctx-content-base'>
              {t('dashboard.sessions.no-sessions-for-date')}
            </span>
            <Button className='whitespace-nowrap !text-xxxs sm:!text-xxs' onClick={() => navigate('detailed-sessions')}>
              {t('dashboard.detailed.sessions-detail-button')}
            </Button>
          </div>
        ) : (
          <>
            <div className='flex items-center justify-end gap-sm pb-nano'>
              {Object.entries(chartConfig).map(([key, config]) => (
                <div key={key} className='flex items-center gap-quark'>
                  <div className='h-3 w-3 shrink-0 rounded-sm' style={{ background: config.color }} />
                  <span className='ts-caption-xxs text-ctx-content-base'>{config.label}</span>
                </div>
              ))}
            </div>

            <div className='flex flex-1'>
              <ChartContainer config={chartConfig} className='w-[60px]'>
                <BarChart responsive accessibilityLayer data={chartData} margin={{ top: 24 }} width={60}>
                  <CartesianGrid vertical={false} />
                  <Bar dataKey='enrolled' fill='transparent' barSize={0} />
                  <YAxis tickLine={false} tickMargin={10} axisLine={false} width={60} />
                  <XAxis dataKey='label' height={62} />
                </BarChart>
              </ChartContainer>

              <div className='flex-1 overflow-x-auto pb-xs'>
                <ChartContainer config={chartConfig} className='w-full' dataLength={chartData.length}>
                  <BarChart responsive accessibilityLayer data={chartData} margin={{ top: 24 }}>
                    <CartesianGrid vertical={false} />
                    <Bar dataKey='enrolled' stackId='a' fill='var(--color-enrolled)' radius={0}>
                      <LabelList
                        dataKey='enrolled'
                        position='insideTop'
                        className='mix-blend-difference text-base-neutral-white'
                      />
                    </Bar>
                    <Bar dataKey='available' stackId='a' fill='var(--color-available)' radius={[8, 8, 0, 0]}>
                      <LabelList dataKey='available' position='top' />
                    </Bar>
                    <XAxis dataKey='label' tickLine={false} tickMargin={10} tick={<SessionAxisTick />} height={62} />
                    <ChartTooltip
                      cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                      content={<ChartTooltipContent />}
                    />
                  </BarChart>
                </ChartContainer>
              </div>
            </div>
          </>
        )}
      </ChartCardContent>

      {chartData.length > 0 && (
        <ChartCardFooter>
          <div className='flex flex-wrap justify-end gap-nano py-nano *:!w-full sm:*:!w-auto'>
            <Button className='whitespace-nowrap !text-xxxs sm:!text-xxs' onClick={() => navigate('detailed-sessions')}>
              {t('dashboard.detailed.sessions-detail-button')}
            </Button>
          </div>
        </ChartCardFooter>
      )}
    </ChartCard>
  );
};
